import React from "react";

import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Theme,
  useTheme,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import CouponCodeDialog from "@/components/CouponCodeDialog";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { Package } from "@/gql/graphql";
import useBooleanToggle from "@/hooks/useBooleanToggle";
import { FragmentType } from "@/utils/type";

import SubscriptionLoader from "./SubscriptionLoader";

const useStyles = makeStyles()((theme: Theme) => ({
  bold: {
    fontWeight: "bold",
  },
  grey: {
    color: theme.palette.grey[600],
  },
}));

type Props = {
  loading: boolean;
  data: FragmentType<typeof currentSubscriptionCardFragment> | undefined;
  onManageSubscription: () => Promise<void>;
};

const currentSubscriptionCardFragment = graphql(`
  fragment CurrentSubscriptionCard on Query {
    coachViewer {
      id
      subscribedPackage
      subscriptionStatus
    }
  }
`);

const CurrentSubscriptionCard: React.FC<Props> = ({
  onManageSubscription,
  loading,
  data,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [couponCodeDialogOpen, setCouponCodeDialogOpen] = useBooleanToggle();
  const currentPlan = (() => {
    switch (data?.coachViewer.subscribedPackage) {
      case Package.Lite:
      case Package.LitePhp:
        return "NutriCoach Lite";
      default:
        return "Free 1 month trial";
    }
  })();
  return (
    <Card>
      <CardContent>
        {loading ? (
          <SubscriptionLoader />
        ) : (
          <>
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              marginBottom={theme.spacing(0.5)}
            >
              <Typography variant="body1" className={classes.bold}>
                My current plan
              </Typography>
              {Features.subscriptionDetailsFieldsEnabled && (
                <Typography variant="subtitle2" className={classes.grey}>
                  Next payment on: 05 Mar, 2022
                </Typography>
              )}
            </Box>
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="center"
              >
                <Typography variant="h5" className={classes.bold} gutterBottom>
                  {currentPlan}
                </Typography>
                {Features.subscriptionDetailsFieldsEnabled && (
                  <Typography variant="subtitle2" className={classes.grey}>
                    From 04 Jan, 2022 to 04 Feb, 2022
                  </Typography>
                )}
              </Box>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Button variant="outlined" onClick={onManageSubscription}>
                  Manage subscription
                </Button>
                <Box marginTop={2}>
                  <Button
                    variant="outlined"
                    onClick={setCouponCodeDialogOpen.setTrue}
                  >
                    Apply coupon code
                  </Button>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
      <CouponCodeDialog
        open={couponCodeDialogOpen}
        onClose={setCouponCodeDialogOpen.setFalse}
      />
    </Card>
  );
};

export default CurrentSubscriptionCard;
